import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { isColorFilter } from '../../../util/colorSwatches';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { ColorSwatchLabel, FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
// Long option lists (designers, colours) get a type-to-filter box. Below this
// many options the list is faster to scan than to search.
const SEARCHABLE_FROM_OPTION_COUNT = 10;

const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, legend, selectedOptions = [], intl } = props;
  const [query, setQuery] = useState('');

  // Colour options get a swatch ahead of their label.
  const showSwatches = isColorFilter(name);
  const isSearchable = options.length >= SEARCHABLE_FROM_OPTION_COUNT;

  const normalized = query.trim().toLowerCase();
  const visibleOptions =
    isSearchable && normalized
      ? options.filter(
        o =>
          `${o.label}`.toLowerCase().includes(normalized) ||
          // Keep anything already ticked visible, so filtering never hides a
          // selection the user has made.
          selectedOptions.includes(o.option)
      )
      : options;

  return (
    <fieldset className={className}>
      {legend ? <legend className={css.accessibilityLegend}>{legend}</legend> : null}
      <div className={css.optionSearchContainer}>
        {isSearchable ? (
          <input
            type="search"
            className={css.optionSearch}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={intl.formatMessage({ id: 'SelectMultipleFilter.searchPlaceholder' })}
            aria-label={intl.formatMessage(
              { id: 'SelectMultipleFilter.searchLabel' },
              { filterLabel: legend }
            )}
          />
        ) : null}
      </div>
      <div className={css.optionScroll}>
        {isSearchable && normalized && visibleOptions.length === 0 ? (
          <p className={css.noMatches}>
            <FormattedMessage id="SelectMultipleFilter.noMatches" values={{ query: query.trim() }} />
          </p>
        ) : null}
        <ul className={css.list}>
          {visibleOptions.map(optionConfig => {
            const { option, label } = optionConfig;
            const fieldId = `${id}.${option}`;
            const optionLabel = showSwatches ? (
              <ColorSwatchLabel option={option} label={label} />
            ) : (
              label
            );
            return (
              <li key={fieldId} className={css.item}>
                <FieldCheckbox id={fieldId} name={name} label={optionLabel} value={option} />
              </li>
            );
          })}
        </ul>
      </div>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Function} props.getAriaLabel - The function to retrieve the aria label for the component
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    openerVariant,
    variant,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilter.labelSelected' },
      { labelText: label, count: selectedOptions.length }
    )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilterPlainForm.labelSelected' },
      { count: selectedOptions.length }
    )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={openerVariant === 'pill' ? label : labelForPopup}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      openerVariant={openerVariant}
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={openerVariant === 'pill' ? css.fieldGroupEditorial : css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
        selectedOptions={selectedOptions}
        intl={intl}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      variant={variant}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={variant === 'editorial' ? css.fieldGroupEditorialPlain : css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
        selectedOptions={selectedOptions}
        intl={intl}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
